import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { useUserContext } from '../../../contexts/UserContext';
import { searchEmployees } from '../../../scripts/dataHandlers';
import axios from 'axios';
import { EmployeeSimple, SearchLabel } from '../../../contexts/interfaces';
import { convertDate } from '../../../utils/convertDate';
import { useErrorContext } from '../../../contexts/ErrorContext';

interface Props {
    setEmployeeSimple: (val: EmployeeSimple | null) => void,
};

const Search = ({ setEmployeeSimple }: Props) => {
    // Set translation
    const { t } = useTranslation();

    const { user } = useUserContext();

    const { handleError } = useErrorContext();

    const [allEmployeesSimple, setAllEmployeesSimple] = useState<EmployeeSimple[]>([]);

    const [searchOptions, setSearchOptions] = useState<SearchLabel[]>([]);

    useEffect(() => {

        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        const doSearch = async () => {

            const userUnits = user?.units.map(unit => unit.id) || [];

            // Search employees
            const res = user && await searchEmployees(user.id, userUnits, source);

            // If response, create and add labelrow to objects in array. Then set array to searchOptions
            if (res?.status === 200 && user) {
                let modifiedArray: SearchLabel[] = [];

                // Create label + value objects from results and set them in modifiedArray
                res.data.result.forEach((emp: EmployeeSimple) => {
                    if (!modifiedArray.find(item => item.value.includes(`,${emp.expenseUnit}`))) {
                        modifiedArray.push({
                            label: `${user.units.find(unit => unit.id === emp.expenseUnit)?.name}` || "",
                            // Combine employee id and expenseUnit ids into one string, separated by comma
                            value: String(`noState_${emp.expenseUnit}`)
                        })
                    }
                    modifiedArray.push({
                        label: `${emp.firstName} ${emp.lastName} (${convertDate(emp.dateOfBirth)})`,
                        // Combine employee id and expenseUnit ids into one string, separated by comma
                        value: String(emp.employeeId) + ',' + String(emp.expenseUnit)
                    })
                });
                // Set result in state of this component
                res && setAllEmployeesSimple([...res.data.result])
                // Set modified array of results in search options state of this component
                res && modifiedArray && setSearchOptions([...modifiedArray]);
            } else {
                handleError(res, "SearchEmployeesFailed");
            }
        };

        user && doSearch();

        return () => source.cancel();
    }, [user])

    const addEmployeeToState = (value: string | null) => {
        if (value && !value.includes('noState_')) {
            // Split string separated by comma
            const arr = value.split(',');
            // First value is empId
            const empId = Number(arr[0]);
            // Second value is empUnitId
            const empUnitId = Number(arr[1]);
            // Find correct employee from list of all employees. matching both id and unit
            const employee = allEmployeesSimple.find(employee => employee.employeeId === empId && employee.expenseUnit === empUnitId);
            // Set selected employee to state
            employee && setEmployeeSimple({ ...employee })
        } else {
            setEmployeeSimple(null);
        }
    };

    const customStyles = {
        option: (provided: any, state: any) => ({
            ...provided,
            color: 'black',
            fontWeight: state.value.includes('noState_') ? '600' : '400',
            backgroundColor: state.value.includes('noState_') ? '#BCBCBC' : 'white',
            border: state.isFocused && !state.value.includes('noState_') ? "1px solid #ED1B24" : "1px solid white",
            cursor: !state.value.includes('noState_') ? "pointer" : "arrow",
        })
    }

    return (
        <Select
            styles={customStyles}
            isSearchable={true}
            options={searchOptions}
            placeholder={t('search.placeholder')}
            onChange={(e) => e && addEmployeeToState(e.value)}
            noOptionsMessage={({ inputValue }) => inputValue ? t("search.noOptions") : null}
            isClearable={true}
        />
    )
};

export default Search;